import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Skeleton, Table } from '@mantine/core'
import { ArrowDown, ArrowUp } from 'lucide-react'

import { useQuery } from '@redwoodjs/web'

import { SegmentedTabs } from 'src/components/Core/SegmentedTabs'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_RETENTION_BY_JOB_LISTING } from 'src/graphql/retentionMetrics.graphql'
import { cn } from 'src/utils'

import AnalyticsCard from '../../components/AnalyticsCard'

import type { RetentionByGroupRow, StatusLogFiltersInput } from 'types/graphql'

const INFO_TEXT = [
  'How well hires for each job listing have stayed, broken out at 30-, 60-, and 90-day milestones.',
  'Use this to spot listings that bleed people early — and listings that quietly produce your strongest retention. A listing that hires 30 applicants but loses 60% by day 30 needs different attention than one losing 60% by day 90.',
  'Toggle the # / % header to switch between the raw count of retained hires and the retention percentage. The Hires column shows the cohort denominator for context.',
].join('\n\n')

type ViewMode = 'count' | 'percent'

const VIEW_MODE_TABS = [
  { value: 'count' as const, label: '#', title: 'Count' },
  { value: 'percent' as const, label: '%', title: 'Percentage' },
] as const

type SortKey =
  | 'groupLabel'
  | 'hires'
  | 'retentionPct30'
  | 'retentionPct60'
  | 'retentionPct90'
type SortDirection = 'asc' | 'desc'

// Re-export type so tests / stories can import a stable row shape without
// needing to construct codegen types by hand.
export type RetentionGroupRow = RetentionByGroupRow

// Format helpers — duplicated inline across the three retention breakdown
// cards (job listing / recruiter / lead source) so each card is
// self-contained. Extracting to a shared module is not worth the cross-file
// coupling for three formatters; if we add a fourth consumer, lift them.
//
// Backend returns retentionPct on a 0–100 scale (e.g. 73.0 for 73%) — do NOT
// multiply by 100 again here.
const fmtPct = (x: number | null | undefined): string =>
  x == null ? '—' : `${Math.round(x)}%`

// Derived retained count = (retentionPct/100) * cohortSize. Returns '—' when
// either operand is null so the cell stays in sync with the percent view.
const fmtCount = (
  pct: number | null | undefined,
  cohortSize: number | null | undefined
): string => {
  if (pct == null || cohortSize == null) return '—'
  return Math.round((pct / 100) * cohortSize).toLocaleString()
}

// Color tone matches the playground sketch:
//   ≥70 → green/semibold (strong retention)
//   <45 → red (poor retention — investigate)
//   null  → muted (insufficient cohort)
//   else  → neutral gray
//
// When cohortSize is < 5 we suppress the red/green signal — a single-person
// cohort showing "0% (n=1)" in red is misleading. Small samples render in the
// neutral gray tone instead.
const toneForPct = (
  x: number | null | undefined,
  cohortSize?: number | null
): string => {
  if (x == null) return 'text-doubleNickel-gray-400'
  if (cohortSize != null && cohortSize < 5) {
    return 'text-doubleNickel-gray-800'
  }
  if (x >= 70) return 'font-semibold text-doubleNickel-success-700'
  if (x < 45) return 'text-doubleNickel-error-600'
  return 'text-doubleNickel-gray-800'
}

const compareNullable = (
  a: number | null | undefined,
  b: number | null | undefined,
  direction: SortDirection
): number => {
  // Nulls always sort to the bottom regardless of direction — same convention
  // as SourceBreakdownCard so users get consistent "missing data is last".
  const aNull = a == null
  const bNull = b == null
  if (aNull && bNull) return 0
  if (aNull) return 1
  if (bNull) return -1
  return direction === 'asc' ? a - b : b - a
}

const sortRows = (
  rows: ReadonlyArray<RetentionGroupRow>,
  sortKey: SortKey,
  direction: SortDirection
): RetentionGroupRow[] => {
  return [...rows].sort((a, b) => {
    if (sortKey === 'groupLabel') {
      return direction === 'asc'
        ? a.groupLabel.localeCompare(b.groupLabel)
        : b.groupLabel.localeCompare(a.groupLabel)
    }
    if (sortKey === 'hires') {
      return direction === 'asc' ? a.hires - b.hires : b.hires - a.hires
    }
    return compareNullable(a[sortKey], b[sortKey], direction)
  })
}

interface SortableHeaderProps {
  label: string
  sortKey: SortKey
  activeSortKey: SortKey
  activeDirection: SortDirection
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}

const SortableHeader = ({
  label,
  sortKey,
  activeSortKey,
  activeDirection,
  onSort,
  align = 'right',
}: SortableHeaderProps) => {
  const isActive = sortKey === activeSortKey
  const Icon = activeDirection === 'asc' ? ArrowUp : ArrowDown
  return (
    <Table.Th
      className={cn(
        'cursor-pointer select-none text-doubleNickel-gray-600 hover:text-doubleNickel-gray-900',
        align === 'right' ? 'text-right' : 'text-left'
      )}
      onClick={() => onSort(sortKey)}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1',
          align === 'right' && 'flex-row-reverse'
        )}
      >
        {label}
        {isActive && <Icon size={12} className="opacity-70" />}
      </span>
    </Table.Th>
  )
}

export interface RetentionByJobListingCardViewProps {
  rows: ReadonlyArray<RetentionGroupRow>
  loading?: boolean
  viewMode?: ViewMode
}

export const RetentionByJobListingCardView = ({
  rows,
  loading = false,
  viewMode = 'percent',
}: RetentionByJobListingCardViewProps) => {
  const [sortKey, setSortKey] = useState<SortKey>('retentionPct90')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDirection(key === 'groupLabel' ? 'asc' : 'desc')
  }

  const sortedRows = useMemo(
    () => sortRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection]
  )

  if (loading) {
    return (
      <Table verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
        <Table.Tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                <Skeleton height={12} width="60%" />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={36} />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={64} />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={64} />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={64} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-xs text-doubleNickel-gray-500">
        No hires in this period.
      </div>
    )
  }

  return (
    <div className="max-h-[360px] overflow-y-auto">
      <Table
        verticalSpacing="xs"
        horizontalSpacing="sm"
        fz="xs"
        stickyHeader
        stickyHeaderOffset={0}
      >
        <Table.Thead className="bg-white">
          <Table.Tr>
            <SortableHeader
              label="Job Listing"
              sortKey="groupLabel"
              activeSortKey={sortKey}
              activeDirection={sortDirection}
              onSort={handleSort}
              align="left"
            />
            <SortableHeader
              label="Hires"
              sortKey="hires"
              activeSortKey={sortKey}
              activeDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHeader
              label="30-Day"
              sortKey="retentionPct30"
              activeSortKey={sortKey}
              activeDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHeader
              label="60-Day"
              sortKey="retentionPct60"
              activeSortKey={sortKey}
              activeDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHeader
              label="90-Day"
              sortKey="retentionPct90"
              activeSortKey={sortKey}
              activeDirection={sortDirection}
              onSort={handleSort}
            />
          </Table.Tr>
        </Table.Thead>
      <Table.Tbody>
        {sortedRows.map((row) => {
          const renderCell = (
            pct: number | null | undefined,
            cohortSize: number | null | undefined
          ): string =>
            viewMode === 'percent' ? fmtPct(pct) : fmtCount(pct, cohortSize)
          return (
            <Table.Tr key={row.groupId} data-testid={`row-${row.groupId}`}>
              <Table.Td className="font-medium text-doubleNickel-gray-800">
                {row.groupLabel}
              </Table.Td>
              <Table.Td className="text-right font-mono tabular-nums font-semibold text-doubleNickel-gray-800">
                {row.hires.toLocaleString()}
              </Table.Td>
              <Table.Td
                className={cn(
                  'text-right font-mono tabular-nums',
                  toneForPct(row.retentionPct30, row.cohortSize30)
                )}
                data-testid={`cell-30-${row.groupId}`}
              >
                {renderCell(row.retentionPct30, row.cohortSize30)}
              </Table.Td>
              <Table.Td
                className={cn(
                  'text-right font-mono tabular-nums',
                  toneForPct(row.retentionPct60, row.cohortSize60)
                )}
                data-testid={`cell-60-${row.groupId}`}
              >
                {renderCell(row.retentionPct60, row.cohortSize60)}
              </Table.Td>
              <Table.Td
                className={cn(
                  'text-right font-mono tabular-nums',
                  toneForPct(row.retentionPct90, row.cohortSize90)
                )}
                data-testid={`cell-90-${row.groupId}`}
              >
                {renderCell(row.retentionPct90, row.cohortSize90)}
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
      </Table>
    </div>
  )
}

export interface RetentionByJobListingCardProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes after
   * the initial mount, the card re-fetches with the same filters. Mirrors
   * SourceBreakdownCard's pattern so refresh wiring stays uniform across the
   * Pipeline Analytics V2 tabs.
   */
  refreshKey: number
}

const RetentionByJobListingCard = ({
  filters,
  refreshKey,
}: RetentionByJobListingCardProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('percent')

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_RETENTION_BY_JOB_LISTING, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  // Skip the initial mount — useQuery already kicked off the first fetch.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const rows = (data?.retentionByJobListing ??
    []) as ReadonlyArray<RetentionGroupRow>

  const headerChildren = (
    <SegmentedTabs
      tabs={VIEW_MODE_TABS}
      value={viewMode}
      onChange={setViewMode}
      ariaLabel="View mode"
    />
  )

  return (
    <AnalyticsCard
      title="Retention by Job Listing"
      info={INFO_TEXT}
      headerChildren={headerChildren}
    >
      <RetentionByJobListingCardView
        rows={rows}
        loading={loading && !data}
        viewMode={viewMode}
      />
    </AnalyticsCard>
  )
}

export default RetentionByJobListingCard
